import classes from 'classnames';
import { AttentionSeeker } from 'react-awesome-reveal';
import { useTranslation } from 'react-i18next';
import Lottie from 'react-lottie';
import MaskedInput, { Mask } from 'react-text-mask';
import React, { memo, useCallback, useRef, useState } from 'react';
import CheckIcon from 'assets/icons/check-circle.svg?react';
import ErrorIcon from 'assets/icons/error-circle.svg?react';
import EyeClosedIcon from 'assets/icons/eye-closed.svg?react';
import EyeOpenIcon from 'assets/icons/eye-open.svg?react';
import useDebounce from 'hooks/useDebounce';
import { EventCollectorProps } from 'hooks/context/EventCollector/types';
import LoadingAnimation from 'assets/lottie/loading.json';
import MagnifyingGlassIcon from 'assets/icons/magnifying-glass.svg?react';
import useEventCollector from 'hooks/context/EventCollector/useEventCollector';
import TextInputEvent from 'hooks/context/EventCollector/events/TextInputEvent';
import styles from './Input.module.scss';
import ErrorMessage from '../ErrorMessage/ErrorMessage';

export type Props = {
  type?: 'text' | 'password' | 'tel' | 'email' | 'number';
  value?: string | number;
  name?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  error?: string;
  errorClassName?: string;
  isTouched?: boolean;
  errorAnimationTrigger?: number; // Pass a different value to re-trigger error animaiton
  placeholder?: string;
  postFix?: string;
  showPasswordReveal?: boolean;
  dataTest?: string;
  stackable?: boolean;
  className?: string;
  classNameField?: string;
  isFramedError?: boolean;
  disabled?: boolean;
  inputMode?:
    | 'none'
    | 'text'
    | 'tel'
    | 'url'
    | 'email'
    | 'numeric'
    | 'decimal'
    | 'search';
  autoComplete?:
    | 'name'
    | 'honorific-prefix'
    | 'given-name'
    | 'additional-name'
    | 'family-name'
    | 'honorific-suffix'
    | 'nickname'
    | 'username'
    | 'email'
    | 'new-password'
    | 'current-password'
    | 'one-time-code'
    | 'organization'
    | 'organization-title'
    | 'street-address'
    | 'address-line1'
    | 'address-line2'
    | 'address-line3'
    | 'address-level1'
    | 'address-level2'
    | 'address-level3'
    | 'address-level4'
    | 'country'
    | 'country-name'
    | 'postal-code'
    | 'cc-name'
    | 'cc-given-name'
    | 'cc-additional-name'
    | 'cc-family-name'
    | 'cc-number'
    | 'cc-exp'
    | 'cc-exp-month'
    | 'cc-exp-year'
    | 'cc-csc'
    | 'cc-type'
    | 'transaction-currency'
    | 'transaction-amount'
    | 'language'
    | 'bday'
    | 'bday-day'
    | 'bday-month'
    | 'bday-year'
    | 'sex'
    | 'url'
    | 'photo'
    | 'tel'
    | 'tel-country-code'
    | 'tel-national'
    | 'tel-area-code'
    | 'tel-local'
    | 'tel-extension';
  mask?: Mask | ((value: string) => Mask);
  guide?: boolean; // Show/Hide mask visual guidance
  withSearchIcon?: boolean;
  isLoading?: boolean;
  onClick?: () => void;
};

const loadingLottieOptions = {
  loop: true,
  autoplay: true,
  animationData: LoadingAnimation,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
};

function Input({
  type = 'text',
  onChange,
  value = '',
  name,
  onBlur,
  error,
  errorClassName,
  isTouched,
  placeholder,
  postFix,
  showPasswordReveal = false,
  dataTest,
  stackable = false,
  className,
  classNameField,
  isFramedError,
  inputMode,
  disabled = false,
  autoComplete,
  mask = false,
  guide = false,
  eventCollector,
  withSearchIcon = false,
  isLoading = false,
  onClick,
}: Props & EventCollectorProps) {
  const { t } = useTranslation('error');
  const { track } = useEventCollector();
  const valueRef = useRef(value);
  const [passwordVisibility, setPasswordVisibility] = useState<
    'password' | 'text'
  >('password');

  const handlePasswordToggle = useCallback(() => {
    setPasswordVisibility(
      passwordVisibility === 'password' ? 'text' : 'password',
    );
  }, [passwordVisibility]);

  const renderInput = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (ref: (inputElement: HTMLElement) => void, props: any) => {
      // Do not use defaultValue to prevent React complaining about switching uncontrolled input to controlled
      const { defaultValue, ...rest } = props;

      return <input ref={ref} data-test={dataTest} {...rest} value={value} />;
    },
    [dataTest, value],
  );

  const handleEventCollector = useCallback(
    (val: string | number) => {
      if (!isTouched || eventCollector?.dontTrack || valueRef.current === val) {
        return;
      }

      track(
        new TextInputEvent({
          value: eventCollector?.dontTrackContent
            ? '<not tracked>'
            : eventCollector?.contentOverride ?? val,
          description: eventCollector?.description ?? name ?? '',
        }),
      );

      valueRef.current = val;
    },
    [
      eventCollector?.contentOverride,
      eventCollector?.description,
      eventCollector?.dontTrack,
      eventCollector?.dontTrackContent,
      isTouched,
      name,
      track,
    ],
  );

  useDebounce(() => handleEventCollector(value), 1000, [value]);

  const showError = isTouched && error;
  const showCheck = isTouched && !error;
  const EyeIcon =
    passwordVisibility === 'password' ? EyeClosedIcon : EyeOpenIcon;

  const loadingAnimation = isLoading && (
    <div>
      <Lottie
        width={80}
        height={80}
        options={loadingLottieOptions}
        isClickToPauseDisabled
      />
    </div>
  );

  return (
    <div className={classes(styles.container, className)}>
      <div
        className={classes(
          styles.field,
          stackable && styles.stackable,
          isFramedError && styles['field-error-framed'],
          showError && styles['field-error'],
          classNameField,
        )}
      >
        <MaskedInput
          mask={mask}
          guide={guide}
          className={classes(
            styles.input,
            isFramedError && styles['input-error'],
          )}
          name={name}
          id={name}
          type={type === 'password' ? passwordVisibility : type}
          onChange={onChange}
          value={value}
          onBlur={onBlur}
          inputMode={inputMode}
          autoComplete={autoComplete}
          render={renderInput}
          disabled={disabled}
          onClick={onClick}
        />

        <label
          htmlFor={name}
          className={classes(
            styles.label,
            isFramedError && styles['label-error'],
            value.toString().length && styles.float,
          )}
        >
          {placeholder}
        </label>

        <div className={styles.badges}>
          {showPasswordReveal && (
            <button
              type="button"
              onClick={handlePasswordToggle}
              className={styles.togglePassword}
            >
              <EyeIcon
                className={classes(styles.badge, styles['password-icon'])}
              />
            </button>
          )}
          {postFix && <span className={styles['post-fix']}>{postFix}</span>}

          {(showError || showCheck) && (
            <div
              className={classes(
                styles.divider,
                isFramedError && styles['divider-error'],
              )}
            />
          )}
          {showError && (
            <AttentionSeeker effect="headShake">
              <ErrorIcon className={styles.badge} />
            </AttentionSeeker>
          )}
          {showCheck && <CheckIcon className={styles.badge} />}
          {withSearchIcon && (
            <MagnifyingGlassIcon
              className={classes(styles.badge, styles.searchIcon)}
              data-test={`${dataTest}-search-icon`}
            />
          )}
          {isLoading && loadingAnimation}
        </div>
      </div>

      {!isFramedError && showError && (
        <div
          className={classes(
            styles['error-message'],
            showError && styles['error-active'],
            errorClassName,
          )}
        >
          <ErrorMessage errors={error} />
        </div>
      )}

      {isFramedError && (
        <span
          className={styles['error-message-be']}
          dangerouslySetInnerHTML={{
            __html: t('validation.failed_to_complete_the_operation'),
          }}
        />
      )}
    </div>
  );
}

const InputMemo = memo(Input);

export default InputMemo;
